/**
 * Notifications Page Loader
 * Pre-loads notification list + unread count before rendering
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth } from './helpers';
import {
  getNotifications,
  getUnreadCount,
  type NotificationItem,
  type UnreadCountResponse,
} from '@/services/notificationService';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';

export interface NotificationsData {
  notifications: NotificationItem[];
  totalCount: number;
  currentPage: number;
  lastPage: number;
  perPage: number;
  unreadCount: UnreadCountResponse;
}

const EMPTY_DATA: NotificationsData = {
  notifications: [],
  totalCount: 0,
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  unreadCount: { total: 0, by_category: {} },
};

export async function notificationsLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    await requireAuth(args.request);

    const url = new URL(args.request.url);
    const page = Number(url.searchParams.get('page')) || 1;
    const read = url.searchParams.get('read') as 'true' | 'false' | null;
    const category = url.searchParams.get('category') ?? undefined;
    const priority = url.searchParams.get('priority') ?? undefined;

    const [listRes, unreadRes] = await Promise.all([
      getNotifications({
        page,
        per_page: 15,
        ...(read ? { read } : {}),
        ...(category ? { category: category as never } : {}),
        ...(priority ? { priority: priority as never } : {}),
      }).catch(() => null),
      getUnreadCount().catch(() => null),
    ]);

    const data: NotificationsData = {
      notifications: listRes?.notifications ?? [],
      totalCount: listRes?.meta?.total ?? 0,
      currentPage: listRes?.meta?.current_page ?? 1,
      lastPage: listRes?.meta?.last_page ?? 1,
      perPage: listRes?.meta?.per_page ?? 15,
      unreadCount: unreadRes ?? { total: 0, by_category: {} },
    };

    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/notifications', loadTime, dataSize);

    return data;
  } catch (error) {
    if (error instanceof Response) {
      throw error;
    }
    trackRouteError('/notifications', error as Error);
    // Return empty data instead of throwing so the page renders
    return EMPTY_DATA;
  }
}
